<template>
  <div
    class="file-pond appearance-extended"
    :class="classes"
    aria-label="Click to select a file to upload"
    aria-role="button"
    @click="open"
    @dragover.prevent
    @drop.prevent="handleDrop"
    @mouseenter="handleHover(true)"
    @mouseleave="handleHover(false)"
  >
    <input
      ref="filePondRef"
      hidden
      type="file"
      :accept="$props.acceptedFileTypes"
      :disabled="$props.disabled"
      :multiple="$props.allowMultiple"
      @change="selectFiles"
      @keyup.enter="open"
    >
    <the-icon
      class="upload-icon"
      :art="isHovering ? 'solid' : 'light'"
      name="upload"
    />
    <span
      class="upload__text body-2"
      v-text="$t('form.file.idle')"
    />

    <basic-text-button
      :disabled="$props.disabled"
      :label="$t(props.allowMultiple ? 'form.file.btn.multiple' : 'form.file.btn.single')"
    />

    <div class="pond-border" />
  </div>
</template>

<script setup>
  import { computed, ref } from 'vue'

  import { ERRORS } from '@shared/helpers/FileHandler'

  import BasicTextButton from '@shared/components/Basic/TextButton'

  // INIT
  const emit = defineEmits(['error', 'upload'])
  const props = defineProps({
    acceptedFileTypes: {
      type: String,
      default: '',
    },

    allowMultiple: {
      type: Boolean,
      default: false,
    },

    disabled: {
      type: Boolean,
      required: true,
    },

    invalid: {
      type: Boolean,
      default: false,
    },

    maxFiles: {
      type: Number,
      default: 1,
    },

    maxFileSize: {
      type: Number,
      required: true,
    },

    numberOfExistingFiles: {
      type: Number,
      default: 0,
    },

    supportingText: {
      type: String,
      default: undefined,
    },
  })

  // DATA
  const filePondRef = ref(null)
  const isHovering = ref(false)

  // COMPUTED
  const classes = computed(() => {
    return {
      'file-pond--disabled': props.disabled,
      'file-pond--error': props.invalid,
    }
  })

  // METHODS
  function selectFiles(event) {
    const { files } = event.target
    if (files && files.length > 0) {
      handleFiles(files)
    }
  }

  function checkFilesValidity(files) {
    const hasSupportedFileType = fileType => props.acceptedFileTypes.includes(fileType)
    const hasSupportedFileSize = fileSize => props.maxFileSize > fileSize

    return Object.values(files).reduce(
      ([valid, invalid], file) => {
        return hasSupportedFileType(file.type) && hasSupportedFileSize(file.size)
          ? [[...valid, file], invalid]
          : [valid, [...invalid, file]]
      },
      [[], []]
    )
  }

  function handleDrop(e) {
    handleFiles(e.dataTransfer.files)
  }

  function handleFiles(files) {
    const [valid, invalid] = checkFilesValidity(files)

    if (invalid.length > 0) {
      emit('error', ERRORS.WRONG_FILE_TYPE)
    }

    if (props.numberOfExistingFiles + valid.length <= props.maxFiles) {
      emit('upload', valid)
    } else {
      emit('error', ERRORS.MAX_FILES)
    }

    if (filePondRef?.value) {
      filePondRef.value.value = ''
    }
  }

  function handleHover(value) {
    if (props.disabled) return
    isHovering.value = value
  }

  function open() {
    if (props.numberOfExistingFiles < props.maxFiles) {
      filePondRef.value && filePondRef.value.click()
    }
  }
</script>

<style scoped>
.file-pond {
  display: flex;
  flex-direction: column;
  row-gap: var(--fixed-spacing-fix-03);
  justify-content: center;
  align-items: center;
  text-align: center;
  height: 196px;
  padding: var(--dotcom-responsive-spacing-res-xl);
  border-radius: var(--fixed-border-radius-fix-02);
  background-color: var(--surface-ext-a);
  transition-property: background-color;
  transition-timing-function: ease-in;
  transition-duration: 300ms;
  position: relative;
  cursor: pointer;

  &--disabled {
    background-color: var(--Interaction-States-disabled-default);
    cursor: default;
  }

  &:not(.file-pond--disabled):hover {
    color: var(--Interaction-States-disabled-default-label);
    background-color: #dedad3;
  }
}

.pond-border {
  position: absolute;
  bottom: 0;
  left: var(--fixed-spacing-fix-04);
  right: var(--fixed-spacing-fix-04);
  height: 1px;
  background-color: var(--on-surface);
}

.file-pond--error .pond-border {
  background-color: var(--error);
}

.file-pond--error .upload__text {
  color: var(--error);
}

.upload-icon {
  font-size: 24px;
}
</style>
